import { For, Show, Suspense, createSignal, Switch, Match } from "solid-js";
import { useSearchParams, A, createAsync, useNavigate, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import {
  Button, Card, CardContent, Badge, Skeleton, EmptyState, Input
} from "~/components/ui";
import { PredictionCalendar } from "~/components/predictions";
import { getPredictions, getChannels, updatePredictionStatus } from "~/server/bff";
import { formatDate, formatRelativeTime } from "~/lib/utils";
import {
  Target, Calendar, List, Search, Filter, CheckCircle, XCircle,
  Clock, AlertTriangle, ChevronRight
} from "lucide-solid";
import type { PredictionStatus } from "~/lib/types";

export const route = {
  preload: () => Promise.all([getPredictions(), getChannels()]),
} satisfies RouteDefinition;

export default function PredictionsPage() {
  if (import.meta.env.DEV) {
    console.info("[debug] render PredictionsPage");
  }
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = createSignal<"list" | "calendar">("list");
  const [searchQuery, setSearchQuery] = createSignal("");
  const navigate = useNavigate();

  const filters = () => ({
    status: searchParams.status as string | undefined,
    channelId: searchParams.channel as string | undefined,
  });

  const predictions = createAsync(() => getPredictions(filters()));
  const channels = createAsync(() => getChannels());

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "due_soon", label: "Due Soon" },
    { value: "overdue", label: "Overdue" },
    { value: "verified", label: "Verified" },
    { value: "failed", label: "Failed" },
  ];

  const filteredPredictions = () => {
    const list = predictions();
    if (!list) return null;
    let preds = list;
    if (searchQuery()) {
      const query = searchQuery().toLowerCase();
      preds = preds.filter(
        (p) =>
          p.event.toLowerCase().includes(query) ||
          p.channel?.name.toLowerCase().includes(query)
      );
    }
    return preds;
  };

  return (
    <AppShell title="Predictions">
      <div class="p-6">
        <PageHeader
          title="Prediction Tracker"
          description="Track forecasts and evaluate creator accuracy"
        />

        <div class="flex items-center justify-between gap-4 mb-6">
          <div class="flex items-center gap-2">
            <div class="relative w-64">
              <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="predictions-search"
                name="predictions-search"
                type="search"
                placeholder="Search predictions..."
                class="pl-9"
                value={searchQuery()}
                onInput={(e) => setSearchQuery(e.currentTarget.value)}
              />
            </div>

            <div class="flex gap-1">
              <For each={statusOptions}>
                {(option) => (
                  <Badge
                    variant={searchParams.status === option.value ? "default" : "outline"}
                    class="cursor-pointer"
                    onClick={() =>
                      setSearchParams({
                        status: searchParams.status === option.value ? undefined : option.value,
                      })
                    }
                  >
                    <StatusFilterIcon status={option.value} />
                    {option.label}
                  </Badge>
                )}
              </For>
            </div>
          </div>

          <div class="flex rounded-md border p-0.5">
            <Button
              variant={viewMode() === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
            >
              <List class="h-4 w-4 mr-1" />
              List
            </Button>
            <Button
              variant={viewMode() === "calendar" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("calendar")}
            >
              <Calendar class="h-4 w-4 mr-1" />
              Calendar
            </Button>
          </div>
        </div>

        <Show
          when={viewMode() === "list"}
          fallback={
            <Suspense fallback={<PredictionsSkeleton />}>
              <Show when={predictions()} fallback={<PredictionsSkeleton />}>
                {(items) => (
                  <PredictionCalendar
                    predictions={items()}
                    onPredictionClick={(pred) => {
                      window.location.href = `/track/predictions/${pred.id}`;
                    }}
                  />
                )}
              </Show>
            </Suspense>
          }
        >
          <Suspense fallback={<PredictionsSkeleton />}>
            <Show when={filteredPredictions()} fallback={<PredictionsSkeleton />}>
              {(items) => (
                <Show
                  when={items().length > 0}
                  fallback={
                    <EmptyState
                      icon={<Target class="h-12 w-12" />}
                      title="No predictions found"
                      description={
                        searchQuery() || searchParams.status
                          ? "Try adjusting your filters"
                          : "Predictions extracted from videos will appear here"
                      }
                    />
                  }
                >
                  <div class="space-y-3">
                    <For each={items()}>
                      {(prediction) => (
                        <Card
                          class="group cursor-pointer hover:border-primary/50 transition-colors"
                          role="link"
                          tabIndex={0}
                          onClick={() => navigate(`/track/predictions/${prediction.id}`)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(`/track/predictions/${prediction.id}`);
                            }
                          }}
                        >
                          <CardContent class="p-4">
                            <div class="flex items-start gap-4">
                              <div class="flex-1 min-w-0">
                                <div class="flex items-start justify-between gap-2 mb-2">
                                  <h3 class="font-medium leading-tight">{prediction.event}</h3>
                                  <StatusBadge status={prediction.status} />
                                </div>

                                <p class="text-sm text-muted-foreground italic line-clamp-2 mb-2">
                                  "{prediction.originalQuote}"
                                </p>

                                <div class="flex items-center gap-4 text-sm text-muted-foreground">
                                  <A
                                    href={`/explore/sources/${prediction.channelId}`}
                                    class="hover:text-foreground"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {prediction.channel?.name}
                                  </A>
                                  <Show when={prediction.targetDate}>
                                    <span class="flex items-center gap-1">
                                      <Calendar class="h-3 w-3" />
                                      Due: {formatDate(prediction.targetDate!)}
                                    </span>
                                  </Show>
                                  <Show when={prediction.confidence}>
                                    <span>Confidence: {prediction.confidence}</span>
                                  </Show>
                                </div>

                                <div class="flex gap-1 mt-2">
                                  <For each={prediction.topicTags}>
                                    {(tag) => (
                                      <Badge variant="secondary" class="text-xs font-mono">
                                        {tag}
                                      </Badge>
                                    )}
                                  </For>
                                </div>
                              </div>

                              <ChevronRight class="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </For>
                  </div>
                </Show>
              )}
            </Show>
          </Suspense>
        </Show>
      </div>
    </AppShell>
  );
}

function StatusFilterIcon(props: { status: string }) {
  return (
    <Switch fallback={<Clock class="h-3 w-3 mr-1" />}>
      <Match when={props.status === "verified"}>
        <CheckCircle class="h-3 w-3 mr-1" />
      </Match>
      <Match when={props.status === "failed"}>
        <XCircle class="h-3 w-3 mr-1" />
      </Match>
      <Match when={props.status === "due_soon"}>
        <AlertTriangle class="h-3 w-3 mr-1" />
      </Match>
      <Match when={props.status === "overdue"}>
        <AlertTriangle class="h-3 w-3 mr-1" />
      </Match>
      <Match when={props.status === "pending"}>
        <Clock class="h-3 w-3 mr-1" />
      </Match>
    </Switch>
  );
}

function StatusBadge(props: { status: string }) {
  return (
    <Switch fallback={<Badge variant="outline"><Clock class="h-3 w-3 mr-1" />Pending</Badge>}>
      <Match when={props.status === "verified"}>
        <Badge variant="success"><CheckCircle class="h-3 w-3 mr-1" />Verified</Badge>
      </Match>
      <Match when={props.status === "failed"}>
        <Badge variant="destructive"><XCircle class="h-3 w-3 mr-1" />Failed</Badge>
      </Match>
      <Match when={props.status === "due_soon"}>
        <Badge variant="warning"><AlertTriangle class="h-3 w-3 mr-1" />Due Soon</Badge>
      </Match>
      <Match when={props.status === "overdue"}>
        <Badge variant="destructive"><AlertTriangle class="h-3 w-3 mr-1" />Overdue</Badge>
      </Match>
    </Switch>
  );
}

function PredictionsSkeleton() {
  return (
    <div class="space-y-3">
      <For each={[1, 2, 3, 4, 5]}>
        {() => (
          <Card>
            <CardContent class="p-4">
              <div class="flex items-start gap-4">
                <div class="flex-1 space-y-2">
                  <Skeleton class="h-5 w-3/4" />
                  <Skeleton class="h-4 w-full" />
                  <Skeleton class="h-4 w-1/2" />
                </div>
                <Skeleton class="h-6 w-20" />
              </div>
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}
